import React from "react";
import { Button, Descriptions, Modal, Tag, Typography } from "antd";

const { Text } = Typography;

const LEAVE_TYPE_LABEL = {
	생휴: "생리휴가",
	오전반차: "오전 반차",
	오후반차: "오후 반차",
	경조사: "경조휴가",
};

const LEAVE_TYPE_COLOR = {
	월차: "blue",
	생휴: "pink",
	사유서: "orange",
	출장: "green",
	시내출장: "green",
	연차: "geekblue",
	경조사: "purple",
	병가: "magenta",
	특별휴가: "gold",
	기타: "lime",
	오전반차: "cyan",
	오후반차: "cyan",
};

const APPROVAL_LABEL = {
	pending: "대기",
	approved: "승인",
	rejected: "반려",
	not_required: "불필요",
};

const REPORT_ONLY_TYPES = new Set(["시내출장", "생휴", "사유서"]);

const jobLabel = (job, gender) => {
	if (job === "목사") return "목사";
	if (job === "전도사") return gender === "남" ? "남전도사" : "여전도사";
	if (job === "교직원") return gender === "남" ? "남직원" : "여직원";
	return job || "—";
};

const textOrDash = (value) => {
	const text = value == null ? "" : String(value).trim();
	return text || "—";
};

const formatTime = (value) => {
	if (!value) return "—";
	return String(value).slice(0, 5);
};

const formatPeriod = (app) => {
	const start = app.start_date || app.leave_date;
	const end = app.end_date || app.leave_date;
	if (!start) return "—";
	if (!end || start === end) return start;
	return `${start} ~ ${end}`;
};

const formatCompanions = (app) => {
	const users = app.additional_users_info || [];
	if (!users.length) return "—";
	return users
		.map((user) => `${user.name} (${jobLabel(user.job, user.gender)})`)
		.join(", ");
};

const formatApproval = (app) => {
	const steps = [...(app.approval_steps || [])].sort(
		(a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0),
	);
	if (steps.length) {
		return steps
			.map((step, index) => {
				const label = step.label || `${step.order || index + 1}단계`;
				const name = step.approver_name ? ` ${step.approver_name}` : "";
				const status = APPROVAL_LABEL[step.status] || step.status || "";
				return `${label}${name} ${status}`.trim();
			})
			.join(" · ");
	}
	const parts = [
		["팀", app.team_leader_status],
		["국", app.office_director_status],
		["당", app.church_leader_status],
	].filter(([, status]) => status);
	if (!parts.length) return "—";
	return parts
		.map(([label, status]) => `${label} ${APPROVAL_LABEL[status] || status}`)
		.join(" · ");
};

const buildRows = (app) => {
	const type = app.leave_type;
	const applicant = app.user_name
		? `${app.user_name} (${jobLabel(app.user_job, app.user_gender)})`
		: "—";
	const rows = [
		["신청자", applicant],
		[
			"휴가 구분",
			<Tag color={LEAVE_TYPE_COLOR[type] || "default"} key="type">
				{LEAVE_TYPE_LABEL[type] || type || "—"}
			</Tag>,
		],
		["신청일", textOrDash(app.application_date)],
		["사용일", formatPeriod(app)],
	];

	if (type === "사유서") {
		rows.push(["세부분류", textOrDash(app.reason_category)]);
		rows.push(["사유", textOrDash(app.reason)]);
		rows.push(["추가 해당인", formatCompanions(app)]);
	} else if (type === "출장") {
		rows.push(["제목", textOrDash(app.trip_title)]);
		rows.push(["목적", textOrDash(app.reason)]);
		rows.push(["배경", textOrDash(app.trip_background)]);
		rows.push(["지역", textOrDash(app.trip_location)]);
		rows.push([
			"숙박",
			app.trip_includes_lodging == null
				? "—"
				: app.trip_includes_lodging
					? "포함"
					: "미포함",
		]);
		rows.push(["교통", textOrDash(app.trip_transport)]);
		rows.push(["비고", textOrDash(app.trip_note)]);
		rows.push(["동행인", formatCompanions(app)]);
	} else if (type === "시내출장") {
		const returnLabel = [
			app.local_trip_return_type,
			app.local_trip_return_time
				? formatTime(app.local_trip_return_time)
				: "",
		]
			.filter(Boolean)
			.join(" · ");
		rows.push(["목적", textOrDash(app.reason)]);
		rows.push(["행선지", textOrDash(app.trip_location)]);
		rows.push(["출발 시각", formatTime(app.local_trip_departure_time)]);
		rows.push(["복귀", returnLabel || "—"]);
		rows.push(["동행인", formatCompanions(app)]);
	} else {
		rows.push(["사유", textOrDash(app.reason)]);
		const companions = formatCompanions(app);
		if (companions !== "—") {
			rows.push(["추가 해당인", companions]);
		}
	}

	if (!REPORT_ONLY_TYPES.has(type)) {
		rows.push(["결재", formatApproval(app)]);
	}
	return rows;
};

const AttendanceLeaveViewModal = ({ open, target, onClose }) => {
	const applications = target?.applications || [];
	const title = [target?.name, target?.date, "신청 내역"]
		.filter(Boolean)
		.join(" · ");

	return (
		<Modal
			title={title}
			open={open}
			onCancel={onClose}
			footer={
				<Button onClick={onClose}>닫기</Button>
			}
			width={640}
			destroyOnClose>
			{applications.length === 0 ? (
				<Text type="secondary">표시할 신청 내역이 없습니다.</Text>
			) : (
				<div className="attendance-leave-view">
					{applications.map((app, index) => (
						<div key={app.id || `${app.leave_type}-${index}`}>
							{applications.length > 1 ? (
								<Text
									strong
									style={{
										display: "block",
										marginBottom: 8,
									}}>
									신청 {index + 1}
								</Text>
							) : null}
							<Descriptions column={1} size="small" bordered>
								{buildRows(app).map(([label, value], rowIndex) => (
									<Descriptions.Item
										key={`${label}-${rowIndex}`}
										label={label}>
										<span className="attendance-leave-view__value">
											{value}
										</span>
									</Descriptions.Item>
								))}
							</Descriptions>
						</div>
					))}
				</div>
			)}
		</Modal>
	);
};

export default AttendanceLeaveViewModal;
